// src/components/GuestTracks.js
import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, onSnapshot, query, where, getDocs } from "firebase/firestore";
import { signInAnonymously } from "firebase/auth";
import { db, auth } from "../firebase";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getSharingGroupByToken, calculateSharingGroupScope } from "../services/sharingGroups";

const GuestTracks = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [runs, setRuns] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [shareGroupScope, setShareGroupScope] = useState(null);
  const location = useLocation();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlShareGroupId = params.get("id");
  const shareGroupToken = params.get("shareGroupToken");
  const [shareGroupId, setShareGroupId] = useState(urlShareGroupId);

  useEffect(() => {
    if (urlShareGroupId) {
      localStorage.setItem("guestShareGroupId", urlShareGroupId);
      setShareGroupId(urlShareGroupId);
      return;
    }
    const cached = localStorage.getItem("guestShareGroupId");
    if (cached) {
      setShareGroupId(cached);
    }
  }, [urlShareGroupId]);

  // Load sharing group scope when token is present
  useEffect(() => {
    if (!shareGroupToken || !user) {
      setShareGroupScope(null);
      return;
    }

    const loadScope = async () => {
      try {
        const groupData = await getSharingGroupByToken(shareGroupToken);
        if (groupData) {
          setGroupName(groupData.name || "");
          const scope = await calculateSharingGroupScope(groupData.id);
          setShareGroupScope(scope);
        }
      } catch (error) {
        console.error("Error loading sharing group scope:", error);
        setShareGroupScope(null);
      }
    };

    loadScope();
  }, [shareGroupToken, user]);

  useEffect(() => {
    if (user || (!shareGroupId && !shareGroupToken)) return;
    signInAnonymously(auth).catch((error) => {
      console.error("Anonymous sign-in error:", error);
    });
  }, [shareGroupId, shareGroupToken, user]);

  useEffect(() => {
    if (!shareGroupId || !user) return;
    const fetchGroupName = async () => {
      const groupDocRef = doc(db, "sharegroups", shareGroupId);
      const groupSnap = await getDoc(groupDocRef);
      if (groupSnap.exists()) {
        setGroupName(groupSnap.data().name);
      } else {
        setGroupName("");
      }
    };
    fetchGroupName();
  }, [shareGroupId]);

  // Fetch runs for sharing group token
  useEffect(() => {
    if (!shareGroupToken || !shareGroupScope || !user) return;

    const fetchRuns = async () => {
      const accessibleRunIds = new Set(shareGroupScope.runs || []);
      if (accessibleRunIds.size === 0) {
        setRuns([]);
        return;
      }

      const runsRef = collection(db, "events");
      const runsSnapshot = await getDocs(runsRef);
      const fetchedRuns = [];

      runsSnapshot.forEach((docSnap) => {
        if (accessibleRunIds.has(docSnap.id)) {
          fetchedRuns.push({ id: docSnap.id, ...docSnap.data() });
        }
      });

      setRuns(fetchedRuns);
    };

    fetchRuns();
  }, [shareGroupToken, shareGroupScope, user]);

  // Fetch runs for old shareGroupId (legacy support)
  useEffect(() => {
    if (!shareGroupId || !user || shareGroupToken) return; // Skip if using new token system
    const runsMap = new Map();
    const unsubscribers = [];

    const upsertRuns = () => {
      setRuns(Array.from(runsMap.values()));
    };

    const subscribeSharedRuns = (collectionName, filterField, filterValue) => {
      if (!filterValue) return;
      const runsRef = collection(db, collectionName);
      const q = query(runsRef, where(filterField, "==", filterValue));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        snapshot.docs.forEach((docSnap) => {
          runsMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
        });
        upsertRuns();
      });
      unsubscribers.push(unsubscribe);
    };

    subscribeSharedRuns("events", "sharedGroupId", shareGroupId);
    subscribeSharedRuns("runs", "sharedGroupId", shareGroupId);
    subscribeSharedRuns("events", "sharedGroupName", groupName);
    subscribeSharedRuns("runs", "sharedGroupName", groupName);

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [groupName, shareGroupId, shareGroupToken, user]);

  const tracks = useMemo(() => {
    if ((!shareGroupId && !shareGroupToken) || runs.length === 0) {
      return [];
    }

    const trackMap = new Map();
    runs.forEach((run) => {
      const trackName = (run.track || run.trackName || "").trim();
      if (!trackName) return;

      const key = trackName.toLowerCase();
      const entry = trackMap.get(key) || {
        name: trackName,
        runCount: 0,
        racerIds: new Set(),
      };

      entry.runCount += 1;
      (run.racers || []).forEach((racer) => {
        if (racer.duration !== null && racer.duration !== undefined) {
          entry.racerIds.add(racer.racerId);
        }
      });

      trackMap.set(key, entry);
    });

    return Array.from(trackMap.values())
      .map((entry) => ({
        name: entry.name,
        runCount: entry.runCount,
        racerCount: entry.racerIds.size,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [runs, shareGroupId, shareGroupToken]);

  const handleViewRuns = (trackName) => {
    const currentParams = new URLSearchParams(location.search);
    const shareId = currentParams.get("id") || shareGroupId;
    const token = currentParams.get("shareGroupToken") || shareGroupToken;
    const tokenParam = token ? `&shareGroupToken=${token}` : "";
    const idParam = shareId ? `&id=${shareId}` : "";
    navigate(`/runs?track=${encodeURIComponent(trackName)}${idParam}${tokenParam}`);
  };

  const handleViewRacers = (trackName) => {
    const currentParams = new URLSearchParams(location.search);
    const shareId = currentParams.get("id") || shareGroupId;
    const token = currentParams.get("shareGroupToken") || shareGroupToken;
    const tokenParam = token ? `&shareGroupToken=${token}` : "";
    const idParam = shareId ? `&id=${shareId}` : "";
    navigate(`/racers?track=${encodeURIComponent(trackName)}${idParam}${tokenParam}`);
  };

  if (!shareGroupId && !shareGroupToken) {
    return (
      <div style={{ padding: "20px" }}>
        <p>Missing share link.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <h2>Tracks in Public Runs</h2>
      {tracks.length === 0 ? (
        <p>No tracks available.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {tracks.map((track) => (
            <div
              key={track.name}
              style={{
                border: "1px solid #eee",
                borderRadius: "8px",
                padding: "12px 16px",
                backgroundColor: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                cursor: "pointer",
              }}
              onClick={() => handleViewRuns(track.name)}
            >
              <div style={{ display: "flex", flexDirection: "column" }}>
                <strong>{track.name}</strong>
                <span style={{ color: "#777", fontSize: "0.9em" }}>
                  {track.runCount} run{track.runCount === 1 ? "" : "s"} •{" "}
                  {track.racerCount} racer{track.racerCount === 1 ? "" : "s"} with
                  results
                </span>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  className="primary-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewRuns(track.name);
                  }}
                >
                  View Runs
                </button>
                <button
                  className="secondary-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewRacers(track.name);
                  }}
                >
                  View Racers
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GuestTracks;
